import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { Toaster } from "sonner";

import "@/index.css";
import { auth, mAuth } from "@/lib/api";
import {
  MERCHANT,
  MERCHANT_LOGIN_PATH,
  MERCHANT_LOGIN_ALIAS,
  ADMIN,
  ADMIN_LOGIN_PATH,
} from "@/lib/routes";

// Admin pages
import LoginPage from "@/pages/Login";
import Overview from "@/pages/Overview";
import Merchants from "@/pages/Merchants";
import MerchantDetail from "@/pages/MerchantDetail";
import Cards from "@/pages/Cards";
import UsersPage from "@/pages/Users";
import CardholderKyc from "@/pages/CardholderKyc";
import CardTypes from "@/pages/CardTypes";
import Fees from "@/pages/Fees";
import Transactions from "@/pages/Transactions";
import AuditLogs from "@/pages/AuditLogs";
import Analytics from "@/pages/Analytics";
import Webhooks from "@/pages/Webhooks";
import ApiDocs from "@/pages/ApiDocs";
import Settings from "@/pages/Settings";
import KybQueue from "@/pages/KybQueue";
import ProviderConfigs from "@/pages/ProviderConfigs";
import BalanceFunding from "@/pages/BalanceFunding";

// Merchant pages
import MerchantLogin from "@/pages/m/MerchantLogin";
import MerchantSignup from "@/pages/m/MerchantSignup";
import MerchantDashboard from "@/pages/m/MerchantDashboard";
import MerchantKyb from "@/pages/m/MerchantKyb";
import MerchantApiKeys from "@/pages/m/MerchantApiKeys";
import MerchantSecurity from "@/pages/m/MerchantSecurity";
import MerchantWebhooks from "@/pages/m/MerchantWebhooks";
import MerchantWebhookEvents from "@/pages/m/MerchantWebhookEvents";
import MerchantCardDetail from "@/pages/m/MerchantCardDetail";
import MerchantWallet from "@/pages/m/MerchantWallet";
import {
  MerchantCards, MerchantUsers, MerchantTransactions, MerchantAudit, MerchantUsage,
} from "@/pages/m/MerchantViews";


function Private({ children }) {
  if (!auth.isAuthed()) return <Navigate to={ADMIN_LOGIN_PATH} replace />;
  return children;
}

function MerchantPrivate({ children }) {
  if (!mAuth.isAuthed()) return <Navigate to={MERCHANT_LOGIN_PATH} replace />;
  return children;
}

function MerchantLoginRoute() {
  if (mAuth.isAuthed()) return <Navigate to={MERCHANT.home} replace />;
  return <MerchantLogin />;
}

/** Old /m/* bookmarks → new merchant paths */
function LegacyMerchantRedirect({ to }) {
  return <Navigate to={to} replace />;
}

function LegacyMerchantCardRedirect() {
  const { cardId } = useParams();
  return <Navigate to={MERCHANT.card(cardId)} replace />;
}

function LegacyAdminMerchantRedirect() {
  const { id } = useParams();
  return <Navigate to={ADMIN.merchant(id)} replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors closeButton />
      <Routes>
        {/* Merchant login (site root) */}
        <Route path={MERCHANT_LOGIN_PATH} element={<MerchantLoginRoute />} />
        <Route path={MERCHANT_LOGIN_ALIAS} element={<MerchantLoginRoute />} />
        <Route path="/m/login" element={<Navigate to={MERCHANT_LOGIN_PATH} replace />} />

        <Route path={MERCHANT.signup} element={<MerchantSignup />} />

        {/* Merchant app */}
        <Route path={MERCHANT.home} element={<MerchantPrivate><MerchantDashboard /></MerchantPrivate>} />
        <Route path={MERCHANT.wallet} element={<MerchantPrivate><MerchantWallet /></MerchantPrivate>} />
        <Route path={MERCHANT.kyb} element={<MerchantPrivate><MerchantKyb /></MerchantPrivate>} />
        <Route path={MERCHANT.apiKeys} element={<MerchantPrivate><MerchantApiKeys /></MerchantPrivate>} />
        <Route path={MERCHANT.security} element={<MerchantPrivate><MerchantSecurity /></MerchantPrivate>} />
        <Route path={MERCHANT.webhooks} element={<MerchantPrivate><MerchantWebhooks /></MerchantPrivate>} />
        <Route path={MERCHANT.webhookEvents} element={<MerchantPrivate><MerchantWebhookEvents /></MerchantPrivate>} />
        <Route path={MERCHANT.cards} element={<MerchantPrivate><MerchantCards /></MerchantPrivate>} />
        <Route path={`${MERCHANT.cards}/:cardId`} element={<MerchantPrivate><MerchantCardDetail /></MerchantPrivate>} />
        <Route path={MERCHANT.users} element={<MerchantPrivate><MerchantUsers /></MerchantPrivate>} />
        <Route
          path={`${MERCHANT.users}/:userId/kyc`}
          element={<MerchantPrivate><Navigate to={MERCHANT.users} replace /></MerchantPrivate>}
        />
        <Route path={MERCHANT.transactions} element={<MerchantPrivate><MerchantTransactions /></MerchantPrivate>} />
        <Route path={MERCHANT.audit} element={<MerchantPrivate><MerchantAudit /></MerchantPrivate>} />
        <Route path={MERCHANT.usage} element={<MerchantPrivate><MerchantUsage /></MerchantPrivate>} />

        {/* Legacy merchant URLs */}
        <Route path="/m" element={<LegacyMerchantRedirect to={MERCHANT.home} />} />
        <Route path="/m/signup" element={<LegacyMerchantRedirect to={MERCHANT.signup} />} />
        <Route path="/m/wallet" element={<LegacyMerchantRedirect to={MERCHANT.wallet} />} />
        <Route path="/m/kyb" element={<LegacyMerchantRedirect to={MERCHANT.kyb} />} />
        <Route path="/m/api-keys" element={<LegacyMerchantRedirect to={MERCHANT.apiKeys} />} />
        <Route path="/m/security" element={<LegacyMerchantRedirect to={MERCHANT.security} />} />
        <Route path="/m/webhooks" element={<LegacyMerchantRedirect to={MERCHANT.webhooks} />} />
        <Route path="/m/webhook-events" element={<LegacyMerchantRedirect to={MERCHANT.webhookEvents} />} />
        <Route path="/m/cards" element={<LegacyMerchantRedirect to={MERCHANT.cards} />} />
        <Route path="/m/cards/:cardId" element={<LegacyMerchantCardRedirect />} />
        <Route path="/m/users" element={<LegacyMerchantRedirect to={MERCHANT.users} />} />
        <Route path="/m/users/:userId/kyc" element={<LegacyMerchantRedirect to={MERCHANT.users} />} />
        <Route path="/m/transactions" element={<LegacyMerchantRedirect to={MERCHANT.transactions} />} />
        <Route path="/m/audit" element={<LegacyMerchantRedirect to={MERCHANT.audit} />} />
        <Route path="/m/usage" element={<LegacyMerchantRedirect to={MERCHANT.usage} />} />

        {/* Admin login (obscure path) */}
        <Route path={ADMIN_LOGIN_PATH} element={<LoginPage />} />

        {/* Admin console */}
        <Route path={ADMIN.home} element={<Private><Overview /></Private>} />
        <Route path={ADMIN.merchants} element={<Private><Merchants /></Private>} />
        <Route path={`${ADMIN.merchants}/:id`} element={<Private><MerchantDetail /></Private>} />
        <Route path={ADMIN.balanceFunding} element={<Private><BalanceFunding /></Private>} />
        <Route path={ADMIN.kybQueue} element={<Private><KybQueue /></Private>} />
        <Route path={ADMIN.providers} element={<Private><ProviderConfigs /></Private>} />
        <Route path={ADMIN.cards} element={<Private><Cards /></Private>} />
        <Route path={ADMIN.users} element={<Private><UsersPage /></Private>} />
        <Route path={`${ADMIN.users}/:userId/kyc`} element={<Private><CardholderKyc /></Private>} />
        <Route path={ADMIN.cardTypes} element={<Private><CardTypes /></Private>} />
        <Route path={ADMIN.fees} element={<Private><Fees /></Private>} />
        <Route path={ADMIN.transactions} element={<Private><Transactions /></Private>} />
        <Route path={ADMIN.auditLogs} element={<Private><AuditLogs /></Private>} />
        <Route path={ADMIN.analytics} element={<Private><Analytics /></Private>} />
        <Route path={ADMIN.webhooks} element={<Private><Webhooks /></Private>} />
        <Route path={ADMIN.apiDocs} element={<Private><ApiDocs /></Private>} />
        <Route path={ADMIN.settings} element={<Private><Settings /></Private>} />

        {/* Legacy admin URLs */}
        <Route path="/dashboard" element={<Navigate to={ADMIN.home} replace />} />
        <Route path="/merchants" element={<Navigate to={ADMIN.merchants} replace />} />
        <Route path="/merchants/:id" element={<LegacyAdminMerchantRedirect />} />
        <Route path="/balance-funding" element={<Navigate to={ADMIN.balanceFunding} replace />} />
        <Route path="/kyb-queue" element={<Navigate to={ADMIN.kybQueue} replace />} />
        <Route path="/providers" element={<Navigate to={ADMIN.providers} replace />} />
        <Route path="/cards" element={<Navigate to={ADMIN.cards} replace />} />
        <Route path="/users" element={<Navigate to={ADMIN.users} replace />} />
        <Route path="/users/:userId/kyc" element={<Navigate to={MERCHANT.users} replace />} />
        <Route path="/card-types" element={<Navigate to={ADMIN.cardTypes} replace />} />
        <Route path="/fees" element={<Navigate to={ADMIN.fees} replace />} />
        <Route path="/transactions" element={<Navigate to={ADMIN.transactions} replace />} />
        <Route path="/audit-logs" element={<Navigate to={ADMIN.auditLogs} replace />} />
        <Route path="/analytics" element={<Navigate to={ADMIN.analytics} replace />} />
        <Route path="/webhooks" element={<Navigate to={ADMIN.webhooks} replace />} />
        <Route path="/api-docs" element={<Navigate to={ADMIN.apiDocs} replace />} />
        <Route path="/settings" element={<Navigate to={ADMIN.settings} replace />} />

        <Route path="*" element={<Navigate to={MERCHANT_LOGIN_PATH} replace />} />
      </Routes>
    </BrowserRouter>
  );
}
